import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

export interface CustomSchemaField {
  key: string;
  rawField?: string;
  title: string;
  explainer: string;
}

export interface CustomSchemaMetadata {
  useOnlyCustomSchema?: boolean;
  note?: string;
}

export interface CustomSchema {
  _metadata?: CustomSchemaMetadata;
  [key: string]:
    | {
        rawField?: string;
        title: string;
        explainer: string;
      }
    | CustomSchemaMetadata
    | undefined;
}

export function useCustomSchema(orgId: string | undefined, sensorId: string | undefined) {
  const [customSchema, setCustomSchema] = useState<CustomSchema>({});
  const [metadata, setMetadata] = useState<CustomSchemaMetadata>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!orgId || !sensorId) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = firebase
      .firestore()
      .collection('orgs')
      .doc(orgId)
      .collection('sensors')
      .doc(sensorId)
      .collection('schema')
      .doc('custom')
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data() as CustomSchema;
            const { _metadata, ...fields } = data || {};
            setMetadata(_metadata || {});
            setCustomSchema(fields || {});
          } else {
            setCustomSchema({});
            setMetadata({});
          }
          setIsLoading(false);
        },
        (err) => {
          console.error('Error fetching custom schema:', err);
          setError(err);
          setIsLoading(false);
        }
      );

    return () => unsubscribe();
  }, [orgId, sensorId]);

  const saveCustomSchema = async (schema: CustomSchema, newMetadata?: CustomSchemaMetadata) => {
    if (!orgId || !sensorId) {
      throw new Error('Missing orgId or sensorId');
    }

    try {
      const dataToSave = {
        ...schema,
        _metadata: newMetadata !== undefined ? newMetadata : metadata,
      };
      await firebase
        .firestore()
        .collection('orgs')
        .doc(orgId)
        .collection('sensors')
        .doc(sensorId)
        .collection('schema')
        .doc('custom')
        .set(dataToSave);
      if (newMetadata !== undefined) {
        setMetadata(newMetadata);
      }
    } catch (err) {
      console.error('Error saving custom schema:', err);
      throw err;
    }
  };

  const addSchemaField = async (
    key: string,
    title: string,
    explainer: string,
    rawField?: string
  ) => {
    const newSchema = {
      ...customSchema,
      [key]: { rawField, title, explainer },
    };
    await saveCustomSchema(newSchema);
  };

  const updateSchemaField = async (
    key: string,
    title: string,
    explainer: string,
    rawField?: string
  ) => {
    const newSchema = {
      ...customSchema,
      [key]: { rawField, title, explainer },
    };
    await saveCustomSchema(newSchema);
  };

  const deleteSchemaField = async (key: string) => {
    const newSchema = { ...customSchema };
    delete newSchema[key];
    await saveCustomSchema(newSchema);
  };

  return {
    customSchema,
    metadata,
    isLoading,
    error,
    saveCustomSchema,
    addSchemaField,
    updateSchemaField,
    deleteSchemaField,
  };
}
